/**
 * The content tree, rendered: nested flex containers, a draggable seam between
 * every pair of siblings, and a drop target on every pane.
 *
 * It renders the tree it is given and reports intentions upward; it owns no state
 * and knows nothing about what a pane contains — WHAT to draw comes in as
 * `renderPane`, so the shell keeps deciding that.
 *
 * PANES ARE ADDRESSED BY LEAF ID, not by what they show. Two panes may hold the
 * same conversation, so "the session pane" is not a thing that can be pointed at;
 * a leaf id is.
 *
 * DRAG COMES FROM THE PANE HEADER, reached through context, so no pane component
 * takes a drag prop and none can forget to. Hit-testing is done on the DOM via
 * `elementFromPoint` and a `data-pane` attribute rather than a registry of
 * rectangles: a registry must be invalidated on every resize and scroll, and a
 * stale rect drops a pane in the wrong place.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import type {
  ContentLeaf,
  ContentNode,
  DropEdge,
  NodePath,
} from '../../features/code/contentTree'
import { ResizeHandle } from './ResizeHandle'

/** How close to an edge counts as "beside this pane" rather than "swap with it". */
const EDGE_BAND = 0.28

interface DragState {
  leafId: string
  x: number
  y: number
  target: { leafId: string; edge: DropEdge } | null
}

interface PaneDragApi {
  leafId: string
  begin: (e: React.PointerEvent) => void
  dragging: boolean
}

const PaneDragContext = createContext<PaneDragApi | null>(null)

/** What Panel.tsx calls to make its header a drag handle. Null outside a tree, so
 *  a pane rendered anywhere else is simply not draggable rather than broken. */
export function usePaneDragHandle(): PaneDragApi | null {
  return useContext(PaneDragContext)
}

/** Which edge of `rect` the pointer is nearest, or `center` when it is inside. */
export function edgeAt(
  rect: { left: number; top: number; width: number; height: number },
  x: number,
  y: number,
): DropEdge {
  if (rect.width <= 0 || rect.height <= 0) return 'center'
  const fx = (x - rect.left) / rect.width
  const fy = (y - rect.top) / rect.height
  const distances: [DropEdge, number][] = [
    ['left', fx],
    ['right', 1 - fx],
    ['top', fy],
    ['bottom', 1 - fy],
  ]
  const [edge, distance] = distances.reduce((best, d) =>
    d[1] < best[1] ? d : best,
  )
  return distance > EDGE_BAND ? 'center' : edge
}

export function ContentPanes({
  tree,
  renderPane,
  onResize,
  onMove,
  maximizedId = null,
  onRestore,
  hidden = false,
}: {
  tree: ContentNode
  renderPane: (leaf: ContentLeaf) => ReactNode
  /** `delta` is a FRACTION of the split's extent, not pixels. */
  onResize: (path: NodePath, index: number, delta: number) => void
  onMove: (leafId: string, targetId: string, edge: DropEdge) => void
  /**
   * The pane filling the space (RFC 0305 §3b). Every other pane stays MOUNTED
   * and is hidden, so a pty, an iframe and a scroll offset survive the round
   * trip. Unmounting them would make restoring a reload.
   */
  maximizedId?: string | null
  /** The chip on a maximized pane restores it. */
  onRestore?: () => void
  /**
   * The whole tree, mounted and not shown: a space the window is not in. The
   * same reasoning as `maximizedId` one level up, so switching spaces keeps
   * every pane's scroll, pty and iframe the way maximizing does.
   */
  hidden?: boolean
}) {
  const [drag, setDrag] = useState<DragState | null>(null)
  const dragRef = useRef<DragState | null>(null)
  dragRef.current = drag

  const begin = useCallback((leafId: string, e: React.PointerEvent) => {
    setDrag({ leafId, x: e.clientX, y: e.clientY, target: null })
  }, [])

  useEffect(() => {
    if (!drag) return

    const resolve = (x: number, y: number): DragState['target'] => {
      const el = document.elementFromPoint(x, y)
      const pane = el?.closest('[data-pane]') as HTMLElement | null
      const leafId = pane?.dataset.pane
      if (!pane || !leafId) return null
      return { leafId, edge: edgeAt(pane.getBoundingClientRect(), x, y) }
    }

    const onMoveEvent = (e: PointerEvent) => {
      setDrag((d) =>
        d
          ? {
              ...d,
              x: e.clientX,
              y: e.clientY,
              target: resolve(e.clientX, e.clientY),
            }
          : d,
      )
    }
    const onUp = (e: PointerEvent) => {
      const current = dragRef.current
      // Resolved again on release: a pointerup can arrive with no preceding move,
      // and dropping on the last KNOWN target would use a position the pointer
      // has already left.
      const target = current ? resolve(e.clientX, e.clientY) : null
      setDrag(null)
      if (current && target && target.leafId !== current.leafId) {
        onMove(current.leafId, target.leafId, target.edge)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrag(null)
    }

    window.addEventListener('pointermove', onMoveEvent)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointermove', onMoveEvent)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      window.removeEventListener('keydown', onKey)
    }
  }, [drag, onMove])

  return (
    <>
      {/* One box per tree, shown or not, so several spaces can sit in one
          column and only the active one takes the room. `display: none` and
          not unmounting: that is the whole point. */}
      <div
        data-space-tree={hidden ? 'hidden' : 'active'}
        className="flex min-h-0 min-w-0 flex-1"
        style={{ display: hidden ? 'none' : undefined }}
      >
        <TreeNode
          node={tree}
          path={[]}
          renderPane={renderPane}
          onResize={onResize}
          drag={drag}
          onBeginDrag={begin}
          maximizedId={maximizedId}
          onRestore={onRestore}
        />
      </div>
      {drag && (
        // The chip follows the pointer, so the gesture reads as carrying the pane
        // rather than as a highlight moving around on its own.
        <div
          className="pointer-events-none fixed z-50 rounded border border-border bg-background px-2 py-1 text-[11px] text-foreground shadow-lg"
          style={{ left: drag.x + 12, top: drag.y + 12 }}
        >
          Moving pane
        </div>
      )}
    </>
  )
}

function TreeNode({
  node,
  path,
  renderPane,
  onResize,
  drag,
  onBeginDrag,
  maximizedId,
  onRestore,
}: {
  node: ContentNode
  path: NodePath
  renderPane: (leaf: ContentLeaf) => ReactNode
  onResize: (path: NodePath, index: number, delta: number) => void
  drag: DragState | null
  onBeginDrag: (leafId: string, e: React.PointerEvent) => void
  maximizedId: string | null
  onRestore?: () => void
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)

  if (node.type === 'leaf') {
    const active =
      drag?.target && drag.target.leafId === node.id ? drag.target.edge : null
    return (
      <PaneDragContext.Provider
        value={{
          leafId: node.id,
          begin: (e) => onBeginDrag(node.id, e),
          dragging: drag?.leafId === node.id,
        }}
      >
        <div
          data-pane={node.id}
          className={`relative flex min-h-0 min-w-0 flex-1 ${
            drag?.leafId === node.id ? 'opacity-60' : ''
          }`}
        >
          {renderPane(node)}
          {active && <DropIndicator edge={active} />}
          {maximizedId === node.id && (
            // Without a sign, the hidden panes read as closed.
            <button
              type="button"
              onClick={onRestore}
              className="-translate-x-1/2 absolute top-1.5 left-1/2 z-20 rounded-full border border-border/60 bg-background/90 px-2 py-0.5 text-[11px] text-muted-foreground shadow-sm hover:text-foreground"
              title="Restore panes"
            >
              Maximized · ⌘⇧↵
            </button>
          )}
        </div>
      </PaneDragContext.Provider>
    )
  }

  const row = node.direction === 'row'
  return (
    <div
      ref={containerRef}
      className={`flex min-h-0 min-w-0 flex-1 overflow-hidden ${
        row ? 'flex-row' : 'flex-col'
      }`}
    >
      {node.children.map((child, index) => {
        // Only a split on the maximized pane's path is ever rendered visibly:
        // everything off it sits inside a hidden slot.
        const onPath = maximizedId ? containsLeaf(child, maximizedId) : null
        const size = maximizedId
          ? 1
          : (node.sizes[index] ?? 1 / node.children.length)
        return (
          <PaneSlot
            // Keyed by the first leaf in the subtree, so React keeps a pane's DOM
            // — its scroll position, any iframe inside it — across rearrangement.
            key={firstLeafId(child)}
            size={size}
            row={row}
            hidden={onPath === false}
            seam={
              !maximizedId && index < node.children.length - 1 ? (
                <ResizeHandle
                  bare
                  orientation={row ? 'vertical' : 'horizontal'}
                  ariaLabel="Resize panes"
                  onDelta={(delta) => {
                    // px -> fraction, measured at drag time: the container's size
                    // changes with the window and a captured value would drift.
                    const rect = containerRef.current?.getBoundingClientRect()
                    const extent = row ? rect?.width : rect?.height
                    if (!extent) return
                    onResize(path, index, delta / extent)
                  }}
                />
              ) : null
            }
          >
            <TreeNode
              node={child}
              path={[...path, index]}
              renderPane={renderPane}
              onResize={onResize}
              drag={drag}
              onBeginDrag={onBeginDrag}
              maximizedId={maximizedId}
              onRestore={onRestore}
            />
          </PaneSlot>
        )
      })}
    </div>
  )
}

function firstLeafId(node: ContentNode): string {
  return node.type === 'leaf' ? node.id : firstLeafId(node.children[0])
}

function containsLeaf(node: ContentNode, id: string): boolean {
  return node.type === 'leaf'
    ? node.id === id
    : node.children.some((child) => containsLeaf(child, id))
}

/** A child's slice of a split, plus the seam after it. */
function PaneSlot({
  size,
  row,
  hidden = false,
  seam,
  children,
}: {
  size: number
  row: boolean
  hidden?: boolean
  seam: ReactNode
  children: ReactNode
}) {
  return (
    <>
      <div
        className="flex min-h-0 min-w-0 overflow-hidden"
        // flex-basis carries the fraction; grow off so the basis is respected,
        // shrink on so the 1px seams come out of the panes rather than
        // overflowing the container.
        style={{
          flexBasis: `${size * 100}%`,
          flexGrow: 0,
          flexShrink: 1,
          flexDirection: row ? 'row' : 'column',
          display: hidden ? 'none' : undefined,
        }}
      >
        {children}
      </div>
      {seam}
    </>
  )
}

/** Where the dragged pane would land: half the target for an edge, all of it for
 *  a swap. */
function DropIndicator({ edge }: { edge: DropEdge }) {
  const geometry: Record<DropEdge, string> = {
    left: 'inset-y-0 left-0 w-1/2',
    right: 'inset-y-0 right-0 w-1/2',
    top: 'inset-x-0 top-0 h-1/2',
    bottom: 'inset-x-0 bottom-0 h-1/2',
    center: 'inset-0',
  }
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute z-40 rounded-lg border-2 border-blue-500/70 bg-blue-500/15 ${geometry[edge]}`}
    />
  )
}
